import apiClient from './api';
import { cachedGet, invalidate } from './apiCache';

export interface ChartOfAccountDto {
  accountId: number;
  accountCode: string;
  accountName: string;
  accountNameAr: string;
  parentAccountId?: number;
  parentAccountName?: string;
  parentAccountCode?: string;
  accountType: string;
  accountNature: string;
  level: number;
  isMainAccount: boolean;
  isFinalAccount: boolean;
  isActive: boolean;
  currencyCode: string;
  allowManualEntry: boolean;
  openingBalance: number;
  currentBalance: number;
  description?: string;
  descriptionAr?: string;
  notes?: string;
  childAccounts: ChartOfAccountDto[];
  hasChildren: boolean;
  childrenCount: number;
}

export interface ChartOfAccountSummaryDto {
  accountId: number;
  accountCode: string;
  accountName: string;
  accountNameAr: string;
  accountType: string;
  isFinalAccount: boolean;
  allowManualEntry: boolean;
  isActive: boolean;
}

export interface ImportAccountsResult {
  totalRecords: number;
  successCount: number;
  updatedCount: number;
  failureCount: number;
  errors: string[];
  updatedAccounts: string[];
  importedAccounts: ChartOfAccountDto[];
  summary: string;
}

class ChartOfAccountsService {
  private baseUrl = '/ChartOfAccounts';

  // Get all accounts (hierarchical) — cached 30s
  async getAll(): Promise<ChartOfAccountDto[]> {
    return cachedGet('coa_all', async () => {
      const response = await apiClient.get(this.baseUrl);
      return response.data.data;
    });
  }

  // Get account by ID
  async getById(id: number): Promise<ChartOfAccountDto> {
    const response = await apiClient.get(`${this.baseUrl}/${id}`);
    return response.data.data;
  }

  // Get flat list of all accounts (for dropdowns/autocomplete)
  async getFlatList(): Promise<ChartOfAccountSummaryDto[]> {
    const hierarchical = await this.getAll();
    return this.flattenAccounts(hierarchical);
  }

  // Get only final accounts (for journal entries)
  async getFinalAccounts(): Promise<ChartOfAccountSummaryDto[]> {
    const all = await this.getFlatList();
    return all.filter(acc => acc.isFinalAccount && acc.isActive);
  }

  // Flatten hierarchical structure - Fixed to avoid duplicates
  private flattenAccounts(accounts: ChartOfAccountDto[]): ChartOfAccountSummaryDto[] {
    const result: ChartOfAccountSummaryDto[] = [];
    const seen = new Set<number>();
    
    const flatten = (accs: ChartOfAccountDto[]) => {
      for (const acc of accs) {
        if (!seen.has(acc.accountId)) {
          seen.add(acc.accountId);
          result.push({
            accountId: acc.accountId,
            accountCode: acc.accountCode,
            accountName: acc.accountName,
            accountNameAr: acc.accountNameAr,
            accountType: acc.accountType,
            isFinalAccount: acc.isFinalAccount,
            allowManualEntry: acc.allowManualEntry,
            isActive: acc.isActive
          });
        }

        if (acc.childAccounts && acc.childAccounts.length > 0) {
          flatten(acc.childAccounts);
        }
      }
    };
    
    flatten(accounts);
    return result;
  }

  /**
   * Get child accounts of a specific parent account
   * الحصول على الحسابات الفرعية لحساب أب معين
   */
  async getByParentId(parentAccountId: number): Promise<ChartOfAccountSummaryDto[]> {
    const allAccounts = await this.getAll();
    return this.getChildrenOfParent(allAccounts, parentAccountId);
  }

  /**
   * Recursively find all children of a specific parent - FIXED to avoid duplicates
   * البحث عن جميع الأبناء لحساب أب معين
   */
  private getChildrenOfParent(accounts: ChartOfAccountDto[], parentId: number): ChartOfAccountSummaryDto[] {
    const result: ChartOfAccountSummaryDto[] = [];
    const seen = new Set<number>();
    
    const findChildren = (accs: ChartOfAccountDto[]) => {
      for (const acc of accs) {
        // Check if this account is a direct child
        if (acc.parentAccountId === parentId && !seen.has(acc.accountId)) {
          seen.add(acc.accountId);
          result.push({
            accountId: acc.accountId,
            accountCode: acc.accountCode,
            accountName: acc.accountName,
            accountNameAr: acc.accountNameAr,
            accountType: acc.accountType,
            isFinalAccount: acc.isFinalAccount,
            allowManualEntry: acc.allowManualEntry,
            isActive: acc.isActive
          });
        }

        // Recursively check children
        if (acc.childAccounts && acc.childAccounts.length > 0) {
          findChildren(acc.childAccounts);
        }
      }
    };
    
    findChildren(accounts);
    return result;
  }

  /**
   * 📤 تصدير شجرة الحسابات إلى Excel
   */
  async exportToExcel(): Promise<void> {
    try {
      const response = await apiClient.get(`${this.baseUrl}/export`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      const dateStr = new Date().toISOString().split('T')[0];
      link.setAttribute('download', `ChartOfAccounts_${dateStr}.xlsx`);
      
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export failed:', error);
      throw error;
    }
  }

  /**
   * 📥 استيراد شجرة الحسابات من Excel
   */
  async importFromExcel(file: File): Promise<ImportAccountsResult> {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await apiClient.post(`${this.baseUrl}/import`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      invalidate('coa_');
      return response.data;
    } catch (error) {
      console.error('Import failed:', error);
      throw error;
    }
  }
}

export const chartOfAccountsService = new ChartOfAccountsService();